import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { CRM_TYPES, CRM_FOLLOWUP_PRESETS } from "@/lib/crm";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function ContactFormDialog({ open, onOpenChange, contact, onSaved, prefill }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const isEdit = !!contact;

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    type: "casting_director",
    company: "",
    email: "",
    phone: "",
    project_context: "",
    notes: "",
    last_contact_at: todayIso(),
    followup_mode: "90",         // "30" | "60" | "90" | "120" | "180" | "custom"
    followup_at: "",             // ISO date (only if custom)
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (contact) {
      const presetMatch = CRM_FOLLOWUP_PRESETS.includes(contact.followup_days);
      setForm({
        first_name: contact.first_name || "",
        last_name: contact.last_name || "",
        type: contact.type || "casting_director",
        company: contact.company || "",
        email: contact.email || "",
        phone: contact.phone || "",
        project_context: contact.project_context || "",
        notes: contact.notes || "",
        last_contact_at: (contact.last_contact_at || todayIso()).slice(0, 10),
        followup_mode: presetMatch ? String(contact.followup_days) : "custom",
        followup_at: presetMatch ? "" : (contact.followup_at || "").slice(0, 10),
      });
    } else {
      setForm({
        first_name: prefill?.first_name || "",
        last_name: prefill?.last_name || "",
        type: prefill?.type || "casting_director",
        company: prefill?.company || "",
        email: prefill?.email || "",
        phone: "",
        project_context: prefill?.project_context || "",
        notes: prefill?.notes || "",
        last_contact_at: todayIso(),
        followup_mode: "90",
        followup_at: "",
      });
    }
  }, [open, contact, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.first_name.trim() || !form.type) {
      toast.error(t("crm.form.required"));
      return;
    }
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      type: form.type,
      company: form.company.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      project_context: form.project_context.trim(),
      notes: form.notes.trim(),
      last_contact_at: form.last_contact_at || todayIso(),
    };
    if (form.followup_mode === "custom") {
      payload.followup_at = form.followup_at || null;
      payload.followup_days = 0;
    } else {
      payload.followup_days = parseInt(form.followup_mode, 10) || 90;
    }
    setSaving(true);
    let r;
    try {
      if (isEdit) {
        r = await api.patch(`/crm/contacts/${contact.contact_id}`, payload);
      } else {
        r = await api.post("/crm/contacts", payload);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail && typeof err.response.data.detail === "string"
        ? err.response.data.detail : t("crm.form.fail"));
      setSaving(false);
      return;
    }
    setSaving(false);
    toast.success(t("crm.form.saved"));
    // Post-save callbacks must never turn a persisted save into an error toast.
    try { onOpenChange?.(false); } catch {}
    try { Promise.resolve(onSaved?.(r?.data)).catch(() => {}); } catch {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="crm-form-dialog"
        className="max-w-2xl bg-stone-950 border-stone-800 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-stone-100">
            {isEdit ? t("crm.form.title_edit") : t("crm.form.title_new")}
          </DialogTitle>
          <DialogDescription className="text-stone-400 text-sm">
            {t("crm.page_subtitle")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-cap">{t("crm.form.first_name")} *</label>
              <input
                data-testid="crm-form-first-name"
                required
                value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)}
                className="script-input w-full"
              />
            </div>
            <div>
              <label className="label-cap">{t("crm.form.last_name")}</label>
              <input
                data-testid="crm-form-last-name"
                value={form.last_name}
                onChange={(e) => set("last_name", e.target.value)}
                className="script-input w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-cap">{t("crm.form.type")} *</label>
              <select
                data-testid="crm-form-type"
                value={form.type}
                onChange={(e) => set("type", e.target.value)}
                className="script-input w-full bg-stone-950"
              >
                {CRM_TYPES.map((k) => (
                  <option key={k} value={k}>{t(`crm.type.${k}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-cap">{t("crm.form.company")}</label>
              <input
                data-testid="crm-form-company"
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                className="script-input w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-cap">{t("crm.form.email")}</label>
              <input
                data-testid="crm-form-email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="script-input w-full"
              />
            </div>
            <div>
              <label className="label-cap">{t("crm.form.phone")}</label>
              <input
                data-testid="crm-form-phone"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="script-input w-full"
              />
            </div>
          </div>

          <div>
            <label className="label-cap">{t("crm.form.project")}</label>
            <input
              data-testid="crm-form-project"
              value={form.project_context}
              onChange={(e) => set("project_context", e.target.value)}
              placeholder={t("crm.form.project_ph")}
              className="script-input w-full"
            />
          </div>

          <div>
            <label className="label-cap">{t("crm.form.notes")}</label>
            <textarea
              data-testid="crm-form-notes"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder={t("crm.form.notes_ph")}
              rows={3}
              className="script-input w-full resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-cap">{t("crm.form.last_contact")}</label>
              <input
                data-testid="crm-form-last-contact"
                type="date"
                value={form.last_contact_at}
                onChange={(e) => set("last_contact_at", e.target.value)}
                className="script-input w-full bg-stone-950"
              />
            </div>
            <div>
              <label className="label-cap">{t("crm.form.followup_period")}</label>
              <select
                data-testid="crm-form-followup-mode"
                value={form.followup_mode}
                onChange={(e) => set("followup_mode", e.target.value)}
                className="script-input w-full bg-stone-950"
              >
                <option value="30">{t("crm.form.days30")}</option>
                <option value="60">{t("crm.form.days60")}</option>
                <option value="90">{t("crm.form.days90")}</option>
                <option value="120">{t("crm.form.days120")}</option>
                <option value="180">{t("crm.form.days180")}</option>
                <option value="custom">{t("crm.form.custom_date")}</option>
              </select>
            </div>
          </div>

          {form.followup_mode === "custom" && (
            <div>
              <label className="label-cap">{t("crm.form.custom_date")}</label>
              <input
                data-testid="crm-form-custom-date"
                type="date"
                value={form.followup_at}
                onChange={(e) => set("followup_at", e.target.value)}
                className="script-input w-full bg-stone-950"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="ghost-btn"
            >
              {t("action.cancel")}
            </button>
            <button
              type="submit"
              data-testid="crm-form-save"
              disabled={saving}
              className="pill-btn"
            >
              {saving ? <><Loader2 size={14} className="animate-spin" /> {t("crm.form.saving")}</> : t("crm.form.save")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
